import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { JwtInterceptor } from './core/auth/jwt.interceptor';
import { ConfigService, configInitializerFactory } from './core/config/config.service';
import { EtagCaptureInterceptor } from './core/concurrency/etag-capture.interceptor';
import { EtagConflictInterceptor } from './core/concurrency/etag-conflict.interceptor';
import { IdempotencyKeyInterceptor } from './core/concurrency/idempotency-key.interceptor';
import { ErrorInterceptor } from './core/error/error.interceptor';
import { TenantInterceptor } from './core/interceptors/tenant.interceptor';
import { TelemetryErrorHandler } from './core/telemetry/telemetry-error-handler';
import { TelemetryService, telemetryInitializerFactory } from './core/telemetry/telemetry.service';
import { SharedModule } from './shared/shared.module';

export function oauthInitializerFactory(oauth: OAuthService): () => Promise<void> {
  return async () => {
    // Use the current page origin so the OAuth redirect comes back to
    // whichever hostname the user opened the SPA on. Without this,
    // `redirectUri` is hardcoded to localhost in the environment files
    // and tenant subdomains (e.g. dutton-gregory.local:4200) get bounced
    // to localhost after Keycloak login.
    const origin = window.location.origin;
    oauth.configure({
      ...environment.oAuthConfig,
      redirectUri: origin,
      postLogoutRedirectUri: origin,
    });
    try {
      // Loads Keycloak's well-known config and, if the URL contains the
      // `?code=...` from a Keycloak redirect-back, exchanges it for a token.
      await oauth.loadDiscoveryDocumentAndTryLogin();
      // Schedule silent refresh before the access token expires so users
      // don't get bounced to /login mid-session.
      oauth.setupAutomaticSilentRefresh();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[OAuth] discovery / tryLogin failed; SPA will boot to /login.', err);
    }
  };
}

export function oauthStorageFactory(): OAuthStorage {
  return localStorage;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    OAuthModule.forRoot(),
    SharedModule,
  ],
  providers: [
    { provide: OAuthStorage, useFactory: oauthStorageFactory },
    // Route uncaught Angular errors to App Insights (no-op when telemetry is off).
    { provide: ErrorHandler, useClass: TelemetryErrorHandler },
    // Init telemetry first so it's live before the other initializers run.
    {
      provide: APP_INITIALIZER,
      useFactory: telemetryInitializerFactory,
      deps: [TelemetryService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: oauthInitializerFactory,
      deps: [OAuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializerFactory,
      deps: [ConfigService],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    // Idempotency key BEFORE the etag interceptor pair so both run on the
    // outgoing request. Capture must run BEFORE conflict so we record the
    // etag from successful responses, then conflict catches 409 mismatches.
    { provide: HTTP_INTERCEPTORS, useClass: IdempotencyKeyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EtagCaptureInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EtagConflictInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
